import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import {
  type ColumnId,
  type MutableColumnDetails,
  type StaticColumnDetails,
} from '@/features/jobs/list/jobs-table-columns'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { createJobsExportAdapter } from '@/features/jobs/list/jobs-export-adapter'

/**
 * The columns the firm dashboard's job table shows.
 */
const firmDashboardJobTableColumns: readonly (StaticColumnDetails &
  MutableColumnDetails)[] = [
  {
    id: 'jobTitle',
    header: 'Job title',
    modifyVisibility: false,
    field: 'Title',
    sortable: true,
    visible: true,
  },
  {
    id: 'jobShortId',
    header: 'ID',
    modifyVisibility: true,
    sortable: false,
    visible: true,
  },
  {
    id: 'submitted',
    header: 'Date submitted',
    modifyVisibility: true,
    field: 'DateSubmitted',
    sortable: true,
    visible: true,
  },
  {
    id: 'clientTeam',
    header: 'Client team',
    modifyVisibility: true,
    field: 'ClientTeamName',
    sortable: true,
    visible: true,
  },
  {
    id: 'progressionStep',
    header: 'Progression',
    modifyVisibility: true,
    sortable: false,
    visible: true,
  },
  {
    id: 'status',
    header: 'Status',
    modifyVisibility: true,
    sortable: false,
    visible: true,
  },
  {
    id: 'action',
    header: '',
    modifyVisibility: false,
    sortable: false,
    visible: true,
  },
]

/**
 * The columns of the firm dashboard's job table and their CSV export.
 */
export class FirmDashboardJobTableColumnsViewModel {
  private readonly _adapter

  /**
   * Columns filtered by appropriate column types.
   */
  readonly filteredColumns = firmDashboardJobTableColumns

  /**
   * The IDs of the columns the table shows, in order.
   */
  readonly visibleColumnIds: readonly ColumnId[] =
    firmDashboardJobTableColumns.map((column) => column.id)

  constructor(
    newAlert: AlertFactory,
    createAlertErrorHandler: AlertErrorHandlerFactory,
    getAllFilteredJobs: () => Promise<SearchableJob[]>,
  ) {
    this._adapter = createJobsExportAdapter(
      getAllFilteredJobs,
      () => this.filteredColumns,
      newAlert,
      createAlertErrorHandler,
    )
  }

  /**
   * Triggers download of jobs data organized into a CSV file.
   */
  exportJobsAsCSV = task.resolved(() => this._adapter.downloadCsv())
}
